import React, { useRef, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'

import { motion, AnimatePresence } from 'framer-motion'
import Classnames from 'classnames'
import produce from 'immer'
import ScrollLock, { TouchScrollable } from 'react-scrolllock'
import { RiCloseCircleLine, RiEditLine } from 'react-icons/ri'

import { useRecoilState, useRecoilValue, useRecoilCallback } from 'recoil'
import {
  showListDetailModal,
  detailModalContentID,
  detailModalContentBlockID,
  detailModalSectionData,
} from '@/store/onepager/onepager'
import { content, content_block } from '@/store/template/content'
import { isBuilder } from '@/store/builder/store'

import ContentEditable from 'react-contenteditable'

import TextareaAutosize from 'react-textarea-autosize'

const ReactQuill = dynamic(() => import('react-quill'), { ssr: false })
import 'react-quill/dist/quill.bubble.css'
// import 'react-quill/dist/quill.snow.css';

function DetailModal() {
  const [showDetailModal, setShowDetailModal] =
    useRecoilState(showListDetailModal)
  const [detailModalCBID, setDetailModalCBID] = useRecoilState(
    detailModalContentBlockID
  )
  const [detailModalCID, setDetailModalCID] =
    useRecoilState(detailModalContentID)
  const [getAllItems, setAllItems] = useRecoilState(content(detailModalCID))
  const [getContent, setContent] = useRecoilState(
    content_block(detailModalCBID)
  )
  const isBuilderApp = useRecoilValue(isBuilder)
  const sectionData = useRecoilValue(detailModalSectionData)
  const [isEditableOnly, setEditableOnly] = useState(true)

  const contentEditable = React.createRef()
  const contentEditableDesc = React.createRef()

  const listItemTitle = useRef('')

  useEffect(() => {
    if (isBuilderApp) {
      // in builder
    } else {
      if (sectionData?.data.readonly == 'true') {
        setEditableOnly(false)
      } else {
        setEditableOnly(true)
      }
    }
  }, [showDetailModal])

  const handleTitleEdit = e => {
    const nextState = produce(getContent, draft => {
      draft.text = e.target.value
    })
    setContent(nextState)
  }

  const handleDescEdit = e => {
    const nextState = produce(getContent, draft => {
      draft.desc = e.target.value
    })
    setContent(nextState)
  }

  const toggleListItemComplete = () => {
    const nextState = produce(getContent, draft => {
      draft.complete = !draft.complete
    })
    setContent(nextState)
  }

  const listItemColorChange = color => {
    const nextState = produce(getContent, draft => {
      draft.color = color
    })
    setContent(nextState)
  }

  const deleteListItem = e => {
    const nextState = produce(getAllItems, draft => {
      const index = draft.blocks.findIndex(task => task === detailModalCBID)
      if (index !== -1) draft.blocks.splice(index, 1)
    })
    setAllItems(nextState)
    setShowDetailModal(false)
  }

  const updateNotes = data => {
    const nextState = produce(getContent, draft => {
      draft.notes = data
    })
    setContent(nextState)
  }

  const preventEnter = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  if (showDetailModal) {
    return (
      <div
        className="fixed cardDetailModal top-0 left-0 w-full h-full z-50 flex flex-col  items-center justify-center overflow-y-auto"
        style={{ zIndex: 99999999 }}
      >
        <ScrollLock isActive={showDetailModal} />
        {/* overlay */}
        <motion.div
          onClick={() => {
            setShowDetailModal(false)
          }}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.15 }}
          variants={{
            hidden: {
              opacity: 0,
            },
            visible: {
              opacity: 0.2,
            },
            exit: {
              opacity: 0,
            },
          }}
          className="absolute top-0 left-0 bg-black w-full h-full opacity-25 cursor-pointer"
        ></motion.div>

        {/* detail modal */}
        <TouchScrollable>
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.15 }}
            variants={{
              hidden: {
                scale: 0.95,
                y: 50,
                opacity: 0,
              },
              visible: {
                scale: 1,
                y: 0,
                opacity: 1,
              },
              exit: {
                scale: 0.95,
                y: 50,
                opacity: 0,
              },
            }}
            className={Classnames(
              'cardModal bg-white rounded-md border-2 border-black shadow-xl z-10 flex  my-10 overflow-y-auto ',
              {
                'border-green-600': getContent.color == 'green',
                'border-blue-600': getContent.color == 'blue',
                'border-red-600': getContent.color == 'red',
                'border-purple-600': getContent.color == 'purple',
                'border-pink-600': getContent.color == 'pink',
                'border-indigo-600': getContent.color == 'indigo',
                'border-teal-600': getContent.color == 'teal',
                'border-yellow-600': getContent.color == 'yellow',
                'border-gray-600': getContent.color == 'gray',
              }
            )}
          >
            <div className="flex-grow flex flex-col  cardModalMain relative">
              <div className="">
                {/* <div
                className={Classnames(
                  '  pb-2 px-5 py-2 border-b border-gray-400 flex items-center justify-between',
                  {
                    'bg-green-100': getContent.color == 'green',
                    'bg-blue-100': getContent.color == 'blue',
                    'bg-red-100': getContent.color == 'red',
                    'bg-purple-100': getContent.color == 'purple',
                    'bg-pink-100': getContent.color == 'pink',
                    'bg-indigo-100': getContent.color == 'indigo',
                    'bg-teal-100': getContent.color == 'teal',
                    'bg-orange-100': getContent.color == 'orange',
                    'bg-yellow-100': getContent.color == 'yellow',
                    'bg-gray-100': getContent.color == 'gray',
                  }
                )}
              >
                <div className="relative rounded-md shadow-sm">
                  <div
                    onClick={toggleListItemComplete}
                    className="outline-none bg-white text-center cursor-pointer hover:shadow-lg p-2 px-3 rounded shadow font-medium w-full display-block"
                  >
                    {getContent.complete && (
                      <span className="line-through">Mark Not Complete</span>
                    )}
                    {!getContent.complete && <span>Mark Complete</span>}
                  </div>
                </div>
              </div> */}

                <div
                  className={Classnames('px-5 md:px-10 py-5 pt-7', {
                    'bg-green-500 text-white': getContent.color == 'green',
                    'bg-blue-500 text-white': getContent.color == 'blue',
                    'bg-red-500 text-white': getContent.color == 'red',
                    'bg-purple-500 text-white': getContent.color == 'purple',
                    'bg-pink-500 text-white': getContent.color == 'pink',
                    'bg-indigo-500 text-white': getContent.color == 'indigo',
                    'bg-teal-500 text-white': getContent.color == 'teal',
                    'bg-orange-500 text-white': getContent.color == 'orange',
                    'bg-yellow-400 text-yellow-900':
                      getContent.color == 'yellow',
                    'bg-gray-500 text-white': getContent.color == 'gray',
                  })}
                >
                  <div
                    onClick={() => {
                      setShowDetailModal(false)
                    }}
                    className="absolute cursor-pointer top-2 right-2 text-black  hover:opacity-75"
                  >
                    <RiCloseCircleLine className="text-xl" />
                  </div>

                  <TextareaAutosize
                    className="block resize-none bg-transparent	 w-full editListItemTitle font-bold text-3xl outline-none"
                    value={getContent.text}
                    placeholder="Title..."
                    onChange={handleTitleEdit}
                    disabled={!isEditableOnly}
                  />
                  <TextareaAutosize
                    className={Classnames(
                      'block resize-none bg-transparent 	placeholder-current w-full editListItemDesc font-medium text-xl outline-none mt-2',
                      {
                        'opacity-50': getContent.desc == '',
                      }
                    )}
                    value={getContent.desc}
                    placeholder="Optional extra text..."
                    onChange={handleDescEdit}
                    disabled={!isEditableOnly}
                  />
                </div>

                {isEditableOnly && (
                  <div
                    className={Classnames(
                      'px-5 md:px-10 py-5 border-t border-gray-300',
                      {
                        'border-green-600': getContent.color == 'green',
                        'border-blue-600': getContent.color == 'blue',
                        'border-red-600': getContent.color == 'red',
                        'border-purple-600': getContent.color == 'purple',
                        'border-pink-600': getContent.color == 'pink',
                        'border-indigo-600': getContent.color == 'indigo',
                        'border-teal-600': getContent.color == 'teal',
                        'border-yellow-600': getContent.color == 'yellow',
                        'border-gray-600': getContent.color == 'gray',
                      }
                    )}
                  >
                    <div className="grid grid-cols-8 gap-3 md:w-2/3">
                      <div
                        onClick={() => {
                          listItemColorChange('green')
                        }}
                        className="h-4 w-4 bg-green-500 cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out rounded-full"
                      ></div>
                      <div
                        onClick={() => {
                          listItemColorChange('blue')
                        }}
                        className="h-4 w-4 bg-blue-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                      ></div>
                      <div
                        onClick={() => {
                          listItemColorChange('red')
                        }}
                        className="h-4 w-4 bg-red-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                      ></div>
                      <div
                        onClick={() => {
                          listItemColorChange('purple')
                        }}
                        className="h-4 w-4 bg-purple-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                      ></div>
                      <div
                        onClick={() => {
                          listItemColorChange('pink')
                        }}
                        className="h-4 w-4 bg-pink-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                      ></div>
                      <div
                        onClick={() => {
                          listItemColorChange('indigo')
                        }}
                        className="h-4 w-4 bg-indigo-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                      ></div>
                      <div
                        onClick={() => {
                          listItemColorChange('yellow')
                        }}
                        className="h-4 w-4 bg-yellow-400 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                      ></div>
                      <div
                        onClick={() => {
                          listItemColorChange('default')
                        }}
                        className="h-4 w-4 bg-white border border-gray-400 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              <div
                className={Classnames(
                  'detailModalQuill mx-0 px-5 md:px-10 border-t border-b border-gray-300 bg-gray-50  py-3 shadow-inner  flex-grow',
                  {
                    'border-green-600 bg-green-50': getContent.color == 'green',
                    'border-blue-600  bg-blue-50': getContent.color == 'blue',
                    'border-red-600  bg-red-50': getContent.color == 'red',
                    'border-purple-600  bg-purple-50':
                      getContent.color == 'purple',
                    'border-pink-600 bg-pink-50': getContent.color == 'pink',
                    'border-indigo-600 bg-indigo-50':
                      getContent.color == 'indigo',
                    'border-teal-600 bg-teal-50': getContent.color == 'teal',
                    'border-yellow-600 bg-yellow-50':
                      getContent.color == 'yellow',
                    'border-gray-600 bg-gray-50': getContent.color == 'gray',
                  }
                )}
              >
                <ReactQuill
                  value={getContent.notes}
                  onChange={updateNotes}
                  theme="bubble"
                  placeholder="Optional notes..."
                  readOnly={!isEditableOnly}
                />
                {/* {onChange = { handleQuillChange }} */}
              </div>

              {isEditableOnly && (
                <div className="my-5 mb-7 flex justify-end mx-5 md:mx-7">
                  <div className="flex-shrink-0 relative rounded-md shadow-sm">
                    <div
                      onClick={deleteListItem}
                      className="outline-none bg-red-50 border-2 border-red-400 text-center cursor-pointer hover:bg-red-500 hover:text-red-100 p-2 px-3 rounded font-medium w-full display-block"
                    >
                      Delete
                    </div>
                  </div>
                </div>
              )}

              {/* <div>{JSON.stringify(getContent)}</div> */}
            </div>

            <div
              className={Classnames(
                'flex-shrink-0 hidden flex flex-col h-full p-10 bg-gray-100 cardModalActions relative transition duration-300 ease-in-out ',
                {
                  'bg-green-100': getContent.color == 'green',
                  'bg-blue-100': getContent.color == 'blue',
                  'bg-red-100': getContent.color == 'red',
                  'bg-purple-100': getContent.color == 'purple',
                  'bg-pink-100': getContent.color == 'pink',
                  'bg-indigo-100': getContent.color == 'indigo',
                  'bg-teal-100': getContent.color == 'teal',
                  'bg-orange-100': getContent.color == 'orange',
                  'bg-yellow-100': getContent.color == 'yellow',
                  'bg-gray-100': getContent.color == 'gray',
                }
              )}
            >
              <div
                onClick={() => {
                  setShowDetailModal(false)
                }}
                className="absolute top-0 right-0  mt-3 mr-3 cursor-pointer hover:opacity-75"
              >
                <div className="h-6 w-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </div>
              </div>

              <div className="cardActionsBlock mb-8">
                <h4 className="cardActionTitle">Color</h4>

                {/* color options */}
                <div className="mt-3 mb-6 grid grid-cols-8 gap-3">
                  <div
                    onClick={() => {
                      listItemColorChange('green')
                    }}
                    className="h-5 w-5 bg-green-500 cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out rounded-full"
                  ></div>
                  <div
                    onClick={() => {
                      listItemColorChange('blue')
                    }}
                    className="h-5 w-5 bg-blue-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                  ></div>
                  <div
                    onClick={() => {
                      listItemColorChange('red')
                    }}
                    className="h-5 w-5 bg-red-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                  ></div>
                  <div
                    onClick={() => {
                      listItemColorChange('purple')
                    }}
                    className="h-5 w-5 bg-purple-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                  ></div>
                  <div
                    onClick={() => {
                      listItemColorChange('pink')
                    }}
                    className="h-5 w-5 bg-pink-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                  ></div>
                  <div
                    onClick={() => {
                      listItemColorChange('indigo')
                    }}
                    className="h-5 w-5 bg-indigo-500 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                  ></div>
                  <div
                    onClick={() => {
                      listItemColorChange('yellow')
                    }}
                    className="h-5 w-5 bg-yellow-400 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                  ></div>
                  <div
                    onClick={() => {
                      listItemColorChange('default')
                    }}
                    className="h-5 w-5 bg-white border border-gray-400 rounded-full cursor-pointer hover:scale-105 transition transform duration-300 ease-in-out"
                  ></div>
                </div>
              </div>

              <div className="cardActionsBlock mb-8">
                <h4 className="cardActionTitle">Actions</h4>
                <div>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div
                      onClick={toggleListItemComplete}
                      className="outline-none bg-white text-center cursor-pointer hover:shadow-lg p-3 rounded shadow font-medium w-full display-block"
                    >
                      {getContent.complete && (
                        <span className="line-through">Mark Not Complete</span>
                      )}
                      {!getContent.complete && <span>Mark Complete</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="cardActionsBlock  absolute bottom-0 right-0 mr-10 mb-10">
                <div>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div
                      onClick={deleteListItem}
                      className="outline-none bg-white text-center cursor-pointer hover:shadow-lg p-3 rounded shadow font-medium w-full display-block"
                    >
                      delete
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </TouchScrollable>
      </div>
    )
  } else {
    return <div className="hidden"></div>
  }
}

export default DetailModal
